import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./components/ui/accordion"
import PieChart from './components/PieChart';

const AppDetails = () => {
  const { appName } = useParams();
  const [appData, setAppData] = useState({});
  const [chartData, setChartData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Add this object with app logos
  const appLogos = {
    'aditya birla': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ0uXxUTmf76IH7IUv3SobYPgnYEnPqlFP3zw&s',
    'paytm': 'https://cdn.icon-icons.com/icons2/730/PNG/512/paytm_icon-icons.com_62778.png',
    'shriram': 'https://play-lh.googleusercontent.com/xAmb2vAFQDzYbmBkTPYdmXEgl5HafdMhWx_auAC9lGlxaTKm2-6joMizdhvXTi1FF7X7',
    'bajaj': 'https://cdn.iconscout.com/icon/free/png-256/free-bajaj-finserv-logo-icon-download-in-svg-png-gif-file-formats--indian-company-companies-pack-logos-icons-2249165.png?f=webp&w=256',
    'navi': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f7/Navi_New_Logo.png/800px-Navi_New_Logo.png',
    'amazon pay': 'https://cdn-icons-png.flaticon.com/512/5968/5968269.png',
    'phonepe': 'https://i.pinimg.com/originals/db/42/53/db4253052cfc0f80ac281486c19f9d57.png',
    'google pay': 'https://i.pinimg.com/564x/8d/ec/e1/8dece15cc40aaf66ed47f6591b639d06.jpg',
    'kiwi': 'https://ibsintelligence.com/wp-content/uploads/2023/08/kiWI.jpg',
    'bharatpe': 'https://bharatpemoney.com/images/BharatPe-Money--logo.jpg',
    'freecharge': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/90/FreeCharge_Logo.png/799px-FreeCharge_Logo.png',
    'genwise': 'https://d2gn4xht817m0g.cloudfront.net/p/media_uploads/images/original/000/716/888/716888-150e2afa70ae2d615668568361f76ea900ee61a8.png?1679027632',
    'niyo': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQxJmXCVUd1atcbJdUl2-tF34ruAUxNCCew5g&s',
    'onecard': 'https://pbs.twimg.com/profile_images/1480798420197462016/FX2rlAeM_400x400.jpg',
    'groww': 'https://logowik.com/content/uploads/images/groww1643.logowik.com.webp',
    'moneytap': 'https://pbs.twimg.com/profile_images/1480763871103049731/z6QHjJpv_400x400.png',
    'pop upi': 'https://play-lh.googleusercontent.com/jqOR__bd_SKMwCV38-A9_9eYi_AOt36bYKmpap9wtZdsaCkSdElYhkwVANGJvOMZGXU',
    'salaryse': 'https://play-lh.googleusercontent.com/aVGFFR3ooPbp_47Us-NTNLfQLQM7KxMphW-KO7EjvUg3AwUiyu41hoMZ9z6WUtTdXcI',
    'samsung pay': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRYYvohcXdmURH03zsZfovye3ZixiWWKUthKQ&s',
    'slice': 'https://res.cloudinary.com/slicepay/image/upload/v1653596191/website/sliceit/assests/press-page/slice-logo-marks.svg',
    'timepay': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSGUYa1VmSKaakhVG8g9lzwbCo-8_QjK1YERg&s',
    'ultracash': 'https://play-lh.googleusercontent.com/msIDCZ9MS-yoG74sjjJX-GsjmaTpArhu7IdOn_Epzx0aN-9pFfPlyk4yfVqL_UKwWOa1',
    'shriram one': 'https://play-lh.googleusercontent.com/xAmb2vAFQDzYbmBkTPYdmXEgl5HafdMhWx_auAC9lGlxaTKm2-6joMizdhvXTi1FF7X7',

  };

  useEffect(() => {
    fetchAppData();
  }, [appName]);

  const fetchAppData = async () => {
    const { data, error } = await supabase
      .from('app_issues')
      .select('*')
      .eq('app_name', decodeURIComponent(appName))
      .single();

    if (error) {
      console.error('Error fetching app data:', error);
    } else {
      setAppData(data);
    }
  };

  const getIssueCount = (issue) => {
    // Remove text within brackets and trim
    const cleanIssue = issue.split('(')[0].trim();
    
    const key = cleanIssue.toLowerCase()
                          .replace(/[^a-z0-9]+/g, '_')  // Replace any non-alphanumeric character with underscore
                          .replace(/^_+|_+$/g, '')      // Remove leading and trailing underscores
                          .replace(/_+/g, '_');         // Replace multiple consecutive underscores with a single one
    return appData[key] || 0;
  };

  const getSubcategoryCount = (subcategory) => {
    return subcategory.issues.reduce((total, issue) => total + getIssueCount(issue), 0);
  };

  const getCategoryCount = (category) => {
    return category.subcategories.reduce((total, subcategory) => total + getSubcategoryCount(subcategory), 0);
  };

  const getTotalCount = () => {
    return categories.reduce((total, category) => total + getCategoryCount(category), 0);
  };

  const formatPercentage = (value, total) => {
    return total > 0 ? ((value / total) * 100).toFixed(2) + '%' : '0%';
  };

  const categories = [
    {
      name: "Performance",
      subcategories: [
        {
          name: "Technical Functionality",
          issues: [
            "App crashes, stops immediately, and closes",
            "Freezes, becomes unresponsive, and gets stuck",
            "Scanner not working properly"
          ]
        },
        {
          name: "App Loading/Response Times",
          issues: [
            "Slow loading of screens, buttons, or features",
            "General sluggishness or unresponsiveness"
          ]
        },
        {
          name: "Login/Registration Issues",
          issues: [
            "OTP problems (not receiving, invalid, delayed)",
            "MPIN setup failures",
            "Account recovery difficulties",
            "Verification limits reached/exceeded",
            "Account blocking/suspension",
            "Account deletion/closure difficulties",
            "Login difficulties from new device"
          ]
        },
        {
          name: "Navigation/User Interface (UI)",
          issues: [
            "Confusing layout",
            "Difficulty finding information",
            "Poor search functionality",
            "Back button issues",
            "Screen rotation problems",
            "Lack of intuitive design",
            "Font size/readability issues",
            "Missing options/features",
            "Blank/white screen issues"
          ]
        },
        {
          name: "Synchronization Issues",
          issues: [
            "Data not syncing correctly across devices",
            "Incorrect or outdated information displayed"
          ]
        },
        {
          name: "Offline Functionality",
          issues: [
            "Limited or no offline access to account details or features"
          ]
        },
        {
          name: "Platform Compatibility",
          issues: [
            "Issues with specific devices, operating systems, or browsers (include version numbers)"
          ]
        },
        {
          name: "Notifications",
          issues: [
            "Excessive or irrelevant notifications",
            "Inability to disable notifications",
            "Missing important notifications (transaction confirmations, rewards, etc.)",
            "Delayed notifications"
          ]
        },
        {
          name: "Accessibility",
          issues: [
            "Issues for users with disabilities (e.g., screen reader compatibility, color contrast)"
          ]
        },
        {
          name: "App Size/Resource Usage",
          issues: [
            "App being too large or resource-intensive",
            "High storage space usage",
            "Excessive RAM usage",
            "Battery drain issues"
          ]
        },
        {
          name: "Background App Behavior",
          issues: [
            "Automatic logouts",
            "App starting automatically",
            "Performance issues when minimized or reopened"
          ]
        }
      ]
    },
    {
      name: "Financial Products & Services",
      subcategories: [
        {
          name: "Money Transfers",
          issues: [
            "Payment processing problems (slow, pending, failed)",
            "Inability to send or receive money",
            "Money deducted but not credited or received",
            "Incorrect transaction history",
            "Limited transaction amounts or frequency",
            "Inability to cancel pending transactions",
            "Difficulties transferring between wallets or accounts",
            "International transaction issues"
          ]
        },
        {
          name: "Account Management",
          issues: [
            "Difficulty viewing account details or statements",
            "Inability to update personal information (address, phone number, email, DOB)",
            "Difficulty closing or deleting account"
          ]
        },
        {
          name: "Cards (Physical/Virtual)",
          issues: [
            "Card activation issues",
            "Card delivery problems (delayed, undelivered, incorrect address)",
            "Inability to view card details (CVV, expiry date, etc.)",
            "Card declined or not working",
            "ATM withdrawal issues",
            "Card not supported on certain platforms",
            "High card fees",
            "Card order issues (freezing, unavailable)"
          ]
        },
        {
          name: "Investments (Digital Gold, Mutual Funds, etc.)",
          issues: [
            "Difficulty buying or selling investments",
            "Inaccurate investment information",
            "Delays in processing investment transactions",
            "Inability to withdraw invested funds",
            "Discrepancies in investment values",
            "Lack of investment options"
          ]
        },
        {
          name: "Other Financial Services (Recharge, Bill Pay, etc.)",
          issues: [
            "Recharge or bill payment failures",
            "Incorrect recharge or bill payment information",
            "High platform fees",
            "Limited recharge or bill payment options"
          ]
        },
        {
          name: "Loans (Application, Management, Repayment)",
          issues: [
            "Slow processing times",
            "Discrepancies between app approvals and branch decisions",
            "In-person requirements despite \"online\" promises",
            "Lack of loan options (personal, business, etc.)",
            "Eligibility criteria unclear or inconsistently applied",
            "Hidden fees or charges not disclosed upfront",
            "Difficulty tracking application status",
            "Difficulty accessing loan details or statements",
            "Lack of part payment options or difficulties with part payments",
            "Confusing or inaccurate loan information displayed",
            "Difficulty with loan closure and obtaining NOCs"
          ]
        },
        {
          name: "Insurance Products",
          issues: [
            "Difficulty purchasing or claiming insurance",
            "Lack of policy information",
            "Delays in policy issuance"
          ]
        }
      ]
    },
    {
      name: "Customer Service & Support",
      subcategories: [
        {
          name: "Contacting Support",
          issues: [
            "Long wait times",
            "Unresponsive chatbots",
            "Difficult to find contact information",
            "Support section or help center not working",
            "Shadow banning"
          ]
        },
        {
          name: "Support Quality",
          issues: [
            "Unhelpful or rude representatives",
            "Lack of knowledge",
            "Unresolved issues",
            "Inaccurate information provided",
            "Slow resolution times",
            "Lack of follow-up"
          ]
        },
        {
          name: "Complaint Resolution",
          issues: [
            "Slow resolution times complaints",
            "Lack of follow-up complaints",
            "Unresolved complaints"
          ]
        }
      ]
    },
    {
      name: "Security & Privacy",
      subcategories: [
        {
          name: "Data Collection",
          issues: [
            "Excessive or unnecessary data collection",
            "Lack of transparency regarding data usage"
          ]
        },
        {
          name: "Data Security",
          issues: [
            "Concerns about app security",
            "Vulnerability to hacking",
            "Lack of two-factor authentication or other security features"
          ]
        },
        {
          name: "Data Sharing",
          issues: [
            "Concerns about data sharing with third parties",
            "Spam calls or messages after providing information"
          ]
        },
        {
          name: "Account Security",
          issues: [
            "Account blocked for suspicious activity without justification"
          ]
        }
      ]
    },
    {
      name: "Rewards & Promotions",
      subcategories: [
        {
          name: "Cashback/Rewards",
          issues: [
            "Promised rewards not received",
            "Difficulty redeeming rewards",
            "Unclear or misleading terms and conditions",
            "Fake or rigged rewards",
            "Low or reduced reward amounts",
            "Irrelevant or useless rewards",
            "Reward expiration tracker issues"
          ]
        },
        {
          name: "Promotional Offers",
          issues: [
            "Misleading or deceptive offers",
            "\"Bait and switch\" tactics",
            "Promotion of gambling or unethical apps"
          ]
        }
      ]
    },
    {
      name: "Debt Collection Practices",
      subcategories: [
        {
          name: "Communication",
          issues: [
            "Aggressive or harassing language",
            "Excessive contact",
            "Contacting family or friends without consent"
          ]
        },
        {
          name: "Timing/Frequency of Contact",
          issues: [
            "Contact before due date",
            "Excessive calls or messages"
          ]
        },
        {
          name: "Adherence to Regulations",
          issues: [
            "Violation of RBI guidelines or other debt collection laws"
          ]
        }
      ]
    },
    {
      name: "Other",
      subcategories: [
        {
          name: "Fees and Charges",
          issues: [
            "High or unexpected fees",
            "Lack of transparency about charges"
          ]
        },
        {
          name: "Comparison with Competitors",
          issues: [
            "Users highlight areas where app falls short compared to competi"
          ]
        },
        {
          name: "Frequent Updates",
          issues: [
            "Too many updates"
          ]
        },
        {
          name: "Information Overload",
          issues: [
            "Too many options, ads, and promotions on home screen"
          ]
        },
        {
          name: "Miscellaneous",
          issues: [
            "Misleading or Deceptive Advertising",
            "Limited Serviceability Area"
          ]
        }
      ]
    }
  ];

  const totalCount = getTotalCount();

  const prepareChartData = (items, countFunction) => {
    const labels = items.map(item => item.name);
    const data = items.map(item => countFunction(item));
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: items.map(() => `hsl(${Math.random() * 360}, 70%, 50%)`),
        },
      ],
    };
  };

  const prepareIssuesChartData = (issues) => {
    const labels = issues;
    const data = issues.map(issue => getIssueCount(issue));
    const backgroundColors = issues.map(() => `hsl(${Math.random() * 360}, 70%, 50%)`);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
        },
      ],
    };
  };

  const handleChartClick = (index) => {
    if (!selectedCategory) {
      setSelectedCategory(categories[index]);
      setChartData(prepareChartData(categories[index].subcategories, getSubcategoryCount));
    } else if (!selectedSubcategory) {
      const newSubcategory = selectedCategory.subcategories[index];
      setSelectedSubcategory(newSubcategory);
      setChartData(prepareIssuesChartData(newSubcategory.issues));
    } else {
      // Reset to top-level categories
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setChartData(prepareChartData(categories, getCategoryCount));
    }
  };

  useEffect(() => {
    if (appData && Object.keys(appData).length > 0) {
      const newChartData = prepareChartData(categories, getCategoryCount);
      console.log('New chart data:', newChartData);
      setChartData(newChartData);
    }
  }, [appData]);

  const getChartTitle = () => {
    if (selectedSubcategory) {
      return `${selectedCategory.name} > ${selectedSubcategory.name}`;
    } else if (selectedCategory) {
      return selectedCategory.name;
    }
    return 'Categories';
  };

  return (
    <div className="AppDetails bg-background text-foreground p-4 font-sans">
      <div className="flex flex-col items-start mb-6">
        <img 
          src={appLogos[appName.toLowerCase()] || 'https://via.placeholder.com/50'} 
          alt={`${appName} logo`} 
          className="w-40 h-20 mb-2 object-contain"
        />
        <h2 className="text-2xl font-bold italic relative inline-block">
          <span className="relative z-10">
            What {decodeURIComponent(appName)} customers have been saying!
          </span>
          <span className="absolute bottom-0 left-0 w-full h-1 bg-[#ffe6a1] z-0"></span>
        </h2>
      </div>
      
      {chartData && (
        <div className="mb-8">
          <PieChart 
            data={chartData} 
            onElementClick={handleChartClick} 
            title={getChartTitle()}
          />
        </div>
      )}

      <h3 className="text-xl font-semibold italic mb-4 p-2 bg-[#ffefc2] rounded inline-block">Category Wise Issues!</h3>

      <Accordion type="single" collapsible className="w-full">
        {categories.map((category, index) => {
          const categoryCount = getCategoryCount(category);
          const categoryPercentage = formatPercentage(categoryCount, totalCount);
          return (
            <AccordionItem value={`item-${index}`} key={category.name}>
              <AccordionTrigger className="text-lg font-semibold w-full">
                <div className="flex justify-between items-center w-full pr-4">
                  <span>{category.name}</span>
                  <span className="text-sm font-normal ml-4">{categoryCount} - {categoryPercentage}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <Accordion type="single" collapsible className="w-full">
                  {category.subcategories.map((subcategory, subIndex) => {
                    const subcategoryCount = getSubcategoryCount(subcategory);
                    const subcategoryPercentage = formatPercentage(subcategoryCount, categoryCount);
                    return (
                      <AccordionItem value={`subitem-${index}-${subIndex}`} key={subcategory.name}>
                        <AccordionTrigger className="text-md w-full">
                          <div className="flex justify-between items-center w-full pr-4">
                            <span>{subcategory.name}</span>
                            <span className="text-sm ml-4">{subcategoryCount} - {subcategoryPercentage}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="list-disc pl-6">
                            {subcategory.issues.map((issue, issueIndex) => {
                              const issueCount = getIssueCount(issue);
                              const issuePercentage = formatPercentage(issueCount, subcategoryCount);
                              return (
                                <li key={issueIndex} className="text-sm text-gray-600 mb-1 flex justify-between items-center">
                                  <span className="font-medium text-gray-800">{issue}</span>
                                  <span className="ml-4">{issueCount} - {issuePercentage}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* New buttons */}
      <div className="mt-8 flex justify-center space-x-4">
        <a
          href="https://drive.google.com/drive/folders/10Xtc_jzJZhi_jmAYFEGZ7vEe176s3fzF?usp=drive_link"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 border border-gray-500 text-gray-500 rounded hover:bg-gray-100 transition-colors"
        >
          Reviews Database
        </a>
      </div>
    </div>
  );
};

export default AppDetails;
